import React, { useMemo, useState } from 'react';
import Dialog from './common/Dialog';
import { buildReportHtml, openReport } from '../utils/reportHtml';
import { exportRowsToExcel } from '../utils/exportExcel';

/**
 * Diálogo de informe a partir de lo que hay en pantalla.
 *
 * `rows` llega con las cabeceras ya humanizadas como claves (el mismo formato
 * del Excel), así que sirve tanto para la tabla como para la exportación.
 */
export default function ReportDialog({
  open,
  onClose,
  title = 'Informe',
  subtitle = '',
  filters = [],
  columns = [],
  rows = [],
  fileBase = 'informe',
  sheetName = 'Informe',
  brand = 'L6 Ecosystem · CMDB',
}) {
  const [titulo, setTitulo] = useState(title);
  const [hidden, setHidden] = useState({});

  const visibles = useMemo(() => columns.filter((c) => !hidden[c]), [columns, hidden]);

  const filtrosActivos = useMemo(
    () => filters.filter((f) => f && String(f.value || '').trim()),
    [filters]
  );

  const filteredRows = useMemo(
    () => rows.map((row) => {
      const clean = {};
      visibles.forEach((c) => { clean[c] = row[c]; });
      return clean;
    }),
    [rows, visibles]
  );

  if (!open) return null;

  const abrirInforme = () => {
    openReport({
      title: titulo || title,
      subtitle,
      filters: filtrosActivos,
      columns: visibles,
      rows: filteredRows,
      brand,
    });
  };

  const descargarExcel = async () => {
    await exportRowsToExcel(filteredRows, { fileBase, sheetName });
  };

  const footer = (
    <>
      <button type="button" className="wol-btn-secondary" onClick={onClose}>Cerrar</button>
      <button type="button" className="wol-btn-secondary" onClick={descargarExcel} disabled={!filteredRows.length}>
        Exportar Excel
      </button>
      <button type="button" className="dv-btn dv-btn-primary" onClick={abrirInforme} disabled={!filteredRows.length}>
        Ver informe / PDF
      </button>
    </>
  );

  return (
    <Dialog title="Crear informe" onClose={onClose} footer={footer} maxWidth={760}>
      <div style={{ display: 'grid', gap: 14 }}>
        <div className="sf2-field">
          <label>Título del informe</label>
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} />
        </div>

        <div>
          <div className="sf2-hint" style={{ marginBottom: 6 }}>
            {rows.length} registro{rows.length === 1 ? '' : 's'} en el informe · {visibles.length} de {columns.length} columnas
          </div>
          {filtrosActivos.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {filtrosActivos.map((f) => (
                <span key={`${f.label}-${f.value}`} className="sf2-pill sf2-pill--info">
                  {f.label}: {f.value}
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <strong style={{ fontSize: 13 }}>Columnas</strong>
            <button type="button" className="wol-btn-secondary" onClick={() => setHidden({})}>Todas</button>
            <button
              type="button"
              className="wol-btn-secondary"
              onClick={() => {
                const next = {};
                columns.forEach((c) => { next[c] = true; });
                setHidden(next);
              }}
            >
              Ninguna
            </button>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            gap: 4,
            maxHeight: 220,
            overflow: 'auto',
            border: '1px solid var(--border-color, #e5e7eb)',
            borderRadius: 8,
            padding: 8,
          }}>
            {columns.map((c) => (
              <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input
                  type="checkbox"
                  checked={!hidden[c]}
                  onChange={(e) => setHidden((prev) => ({ ...prev, [c]: !e.target.checked }))}
                />
                {c}
              </label>
            ))}
          </div>
        </div>

        <details>
          <summary style={{ cursor: 'pointer', fontSize: 12 }}>Vista previa del HTML</summary>
          <iframe
            title="Vista previa del informe"
            style={{ width: '100%', height: 320, border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 8 }}
            srcDoc={buildReportHtml({
              title: titulo || title,
              subtitle,
              filters: filtrosActivos,
              columns: visibles,
              rows: filteredRows.slice(0, 40),
              brand,
            })}
          />
          {filteredRows.length > 40 && (
            <div className="sf2-hint">La vista previa muestra las primeras 40 filas; el informe lleva todas.</div>
          )}
        </details>
      </div>
    </Dialog>
  );
}
